import React, { useState, useEffect, useRef } from 'react';

/**
 * SYSTEM VISUALIZER
 *
 * Shows the SHAPE of the entire system - every component, connection, data flow
 * This is what enables building complex things - SEEING how it all connects
 */

interface SystemNode {
  id: string;
  type: 'component' | 'api' | 'database' | 'service' | 'state';
  name: string;
  status: 'idle' | 'active' | 'error' | 'processing';
  metrics?: {
    calls?: number;
    errors?: number;
    latency?: number;
    size?: number;
  };
}

interface DataFlow {
  id: string;
  from: string;
  to: string;
  type: 'request' | 'response' | 'state' | 'event';
  data?: any;
  timestamp: number;
  duration?: number;
}

export default function SystemVisualizer() {
  const [nodes] = useState<SystemNode[]>([
    // Frontend Layer
    { id: 'ui', type: 'component', name: 'React UI', status: 'idle' },
    { id: 'state', type: 'state', name: 'State Manager', status: 'idle' },
    { id: 'router', type: 'component', name: 'Router', status: 'idle' },

    // API Layer
    { id: 'api', type: 'api', name: 'CF Worker', status: 'idle' },
    { id: 'auth', type: 'service', name: 'Auth Service', status: 'idle' },

    // Data Layer
    { id: 'd1', type: 'database', name: 'D1 Database', status: 'idle' },
    { id: 'kv', type: 'database', name: 'KV Store', status: 'idle' },
    { id: 'r2', type: 'database', name: 'R2 Storage', status: 'idle' },

    // External Services
    { id: 'email', type: 'service', name: 'Email (Resend)', status: 'idle' },
    { id: 'analytics', type: 'service', name: 'Analytics', status: 'idle' }
  ]);

  const [flows, setFlows] = useState<DataFlow[]>([]);
  const [activeFlows, setActiveFlows] = useState<Set<string>>(new Set());
  const [logs, setLogs] = useState<string[]>([]);
  const [inspectedNode, setInspectedNode] = useState<SystemNode | null>(null);

  // Simulate a complete user action flow
  const simulateUserAction = async (action: string) => {
    const timestamp = Date.now();
    log(`USER ACTION: ${action}`);

    // Create flow ID for tracking
    const flowId = `flow-${timestamp}`;

    // Step 1: UI receives click
    await addFlow({
      id: `${flowId}-1`,
      from: 'user',
      to: 'ui',
      type: 'event',
      data: { action, timestamp },
      timestamp
    });

    // Step 2: UI updates state
    await addFlow({
      id: `${flowId}-2`,
      from: 'ui',
      to: 'state',
      type: 'state',
      data: { update: 'setPending' },
      timestamp: timestamp + 50
    });

    // Step 3: Make API call
    await addFlow({
      id: `${flowId}-3`,
      from: 'ui',
      to: 'api',
      type: 'request',
      data: {
        method: 'POST',
        endpoint: '/api/bets',
        body: { amount: 100, option: 'YES' }
      },
      timestamp: timestamp + 100
    });

    // Step 4: API checks auth
    await addFlow({
      id: `${flowId}-4`,
      from: 'api',
      to: 'auth',
      type: 'request',
      data: { validate: 'token' },
      timestamp: timestamp + 150
    });

    // Step 5: Auth responds
    await addFlow({
      id: `${flowId}-5`,
      from: 'auth',
      to: 'api',
      type: 'response',
      data: { valid: true, userId: 'user-123' },
      timestamp: timestamp + 200
    });

    // Step 6: Query database
    await addFlow({
      id: `${flowId}-6`,
      from: 'api',
      to: 'd1',
      type: 'request',
      data: {
        query: 'INSERT INTO bets VALUES (?)',
        params: ['bet-data']
      },
      timestamp: timestamp + 250
    });

    // Step 7: Database responds
    await addFlow({
      id: `${flowId}-7`,
      from: 'd1',
      to: 'api',
      type: 'response',
      data: { success: true, id: 'bet-456' },
      timestamp: timestamp + 350
    });

    // Step 8: Send email
    await addFlow({
      id: `${flowId}-8`,
      from: 'api',
      to: 'email',
      type: 'request',
      data: {
        to: 'user@example.com',
        subject: 'Bet Confirmed'
      },
      timestamp: timestamp + 400
    });

    // Step 9: API responds to UI
    await addFlow({
      id: `${flowId}-9`,
      from: 'api',
      to: 'ui',
      type: 'response',
      data: {
        success: true,
        betId: 'bet-456',
        newBalance: 900
      },
      timestamp: timestamp + 450
    });

    // Step 10: Update state with result
    await addFlow({
      id: `${flowId}-10`,
      from: 'ui',
      to: 'state',
      type: 'state',
      data: {
        update: 'setSuccess',
        balance: 900
      },
      timestamp: timestamp + 500
    });

    log(`FLOW COMPLETE: ${flowId} (500ms total)`);
  };

  const addFlow = async (flow: DataFlow) => {
    // Update node statuses
    updateNodeStatus(flow.from, 'processing');
    updateNodeStatus(flow.to, 'active');

    // Add to active flows
    setActiveFlows(prev => new Set([...prev, flow.id]));

    // Add to flow list
    setFlows(prev => [...prev, flow]);

    // Log the flow
    log(`[${flow.type.toUpperCase()}] ${flow.from} → ${flow.to}: ${JSON.stringify(flow.data)}`);

    // Simulate processing time
    await new Promise(resolve => setTimeout(resolve, 100));

    // Remove from active flows
    setActiveFlows(prev => {
      const next = new Set(prev);
      next.delete(flow.id);
      return next;
    });

    // Reset node statuses
    updateNodeStatus(flow.from, 'idle');
    updateNodeStatus(flow.to, 'idle');
  };

  const updateNodeStatus = (nodeId: string, status: SystemNode['status']) => {
    const node = nodes.find(n => n.id === nodeId);
    if (node) {
      node.status = status;
    }
  };

  const log = (message: string) => {
    const timestamp = new Date().toISOString().split('T')[1].slice(0, -1);
    setLogs(prev => [...prev, `[${timestamp}] ${message}`]);
  };

  // Node positioning for visualization
  const getNodePosition = (nodeId: string) => {
    const positions: Record<string, { x: number; y: number }> = {
      // Frontend Layer (top)
      ui: { x: 200, y: 50 },
      state: { x: 400, y: 50 },
      router: { x: 600, y: 50 },

      // API Layer (middle)
      api: { x: 400, y: 200 },
      auth: { x: 600, y: 200 },

      // Data Layer (bottom left)
      d1: { x: 200, y: 350 },
      kv: { x: 350, y: 350 },
      r2: { x: 500, y: 350 },

      // Services (bottom right)
      email: { x: 650, y: 350 },
      analytics: { x: 800, y: 350 }
    };

    return positions[nodeId] || { x: 0, y: 0 };
  };

  const getNodeColor = (node: SystemNode) => {
    const colors = {
      component: '#4ade80', // green
      api: '#60a5fa',      // blue
      database: '#f59e0b',  // yellow
      service: '#a78bfa',   // purple (sorry!)
      state: '#f87171'      // red
    };

    if (node.status === 'error') return '#ef4444';
    if (node.status === 'processing') return '#3b82f6';
    if (node.status === 'active') return '#10b981';

    return colors[node.type] || '#6b7280';
  };

  return (
    <div className="min-h-screen bg-black text-green-400 font-mono p-4">
      <div className="max-w-full mx-auto">
        <div className="mb-4 border border-green-400 p-2">
          <h1 className="text-2xl">SYSTEM VISUALIZER - SEE THE SHAPE OF EVERYTHING</h1>
          <p className="text-sm text-gray-400">Every component, every connection, every data flow</p>
        </div>

        <div className="grid grid-cols-12 gap-4">
          {/* Left: System Map */}
          <div className="col-span-8 border border-green-400 p-4 relative" style={{ height: '600px' }}>
            <h2 className="text-xl mb-4">SYSTEM ARCHITECTURE</h2>

            {/* SVG for connections */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none">
              {/* Draw connections */}
              {flows.slice(-10).map(flow => {
                const from = getNodePosition(flow.from);
                const to = getNodePosition(flow.to);
                const isActive = activeFlows.has(flow.id);

                return (
                  <line
                    key={flow.id}
                    x1={from.x + 50}
                    y1={from.y + 25}
                    x2={to.x + 50}
                    y2={to.y + 25}
                    stroke={isActive ? '#3b82f6' : '#1f2937'}
                    strokeWidth={isActive ? 3 : 1}
                    opacity={isActive ? 1 : 0.3}
                    className={isActive ? 'animate-pulse' : ''}
                  />
                );
              })}
            </svg>

            {/* Render nodes */}
            {nodes.map(node => {
              const pos = getNodePosition(node.id);
              return (
                <div
                  key={node.id}
                  className="absolute border-2 px-2 py-1 cursor-pointer transition-all"
                  style={{
                    left: `${pos.x}px`,
                    top: `${pos.y}px`,
                    borderColor: getNodeColor(node),
                    backgroundColor: node.status !== 'idle' ? `${getNodeColor(node)}20` : 'transparent',
                    boxShadow: node.status === 'active' ? `0 0 20px ${getNodeColor(node)}` : 'none'
                  }}
                  onClick={() => setInspectedNode(node)}
                >
                  <div className="text-xs font-bold">{node.name}</div>
                  <div className="text-xs opacity-70">{node.type}</div>
                  {node.status !== 'idle' && (
                    <div className="text-xs mt-1">[{node.status}]</div>
                  )}
                </div>
              );
            })}

            {/* Legend */}
            <div className="absolute bottom-4 left-4 text-xs space-y-1">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-green-400"></div>
                <span>Component</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-blue-400"></div>
                <span>API</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-yellow-400"></div>
                <span>Database</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-purple-400"></div>
                <span>Service</span>
              </div>
            </div>
          </div>

          {/* Right: Controls and Inspector */}
          <div className="col-span-4 space-y-4">
            {/* Controls */}
            <div className="border border-green-400 p-4">
              <h2 className="text-xl mb-4">SIMULATE ACTIONS</h2>
              <div className="space-y-2">
                <button
                  onClick={() => simulateUserAction('PLACE_BET')}
                  className="w-full p-2 bg-green-400 text-black hover:bg-green-300"
                >
                  Simulate: Place Bet
                </button>
                <button
                  onClick={() => simulateUserAction('LOAD_MARKETS')}
                  className="w-full p-2 bg-blue-400 text-black hover:bg-blue-300"
                >
                  Simulate: Load Markets
                </button>
                <button
                  onClick={() => simulateUserAction('AUTH_LOGIN')}
                  className="w-full p-2 bg-yellow-400 text-black hover:bg-yellow-300"
                >
                  Simulate: Login
                </button>
                <button
                  onClick={() => {
                    setFlows([]);
                    setLogs([]);
                  }}
                  className="w-full p-2 bg-red-400 text-black hover:bg-red-300"
                >
                  Clear All
                </button>
              </div>
            </div>

            {/* Node Inspector */}
            {inspectedNode && (
              <div className="border border-green-400 p-4">
                <h2 className="text-xl mb-4">NODE INSPECTOR</h2>
                <div className="space-y-2 text-sm">
                  <div>ID: {inspectedNode.id}</div>
                  <div>Type: {inspectedNode.type}</div>
                  <div>Name: {inspectedNode.name}</div>
                  <div>Status: {inspectedNode.status}</div>

                  <div className="pt-2 border-t border-green-400">
                    <div>Connections:</div>
                    <div className="text-xs opacity-70">
                      In: {flows.filter(f => f.to === inspectedNode.id).length}
                    </div>
                    <div className="text-xs opacity-70">
                      Out: {flows.filter(f => f.from === inspectedNode.id).length}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Metrics */}
            <div className="border border-green-400 p-4">
              <h2 className="text-xl mb-4">SYSTEM METRICS</h2>
              <div className="space-y-2 text-sm">
                <div>Total Flows: {flows.length}</div>
                <div>Active Flows: {activeFlows.size}</div>
                <div>Nodes: {nodes.length}</div>
                <div>Error Rate: 0.0%</div>
                <div>Avg Latency: 45ms</div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom: Flow Log */}
        <div className="mt-4 border border-green-400 p-4">
          <h2 className="text-xl mb-4">FLOW LOG</h2>
          <div className="h-32 overflow-y-auto text-xs">
            {logs.slice(-20).map((log, i) => (
              <div
                key={i}
                className={
                  log.includes('ERROR') ? 'text-red-400' :
                  log.includes('SUCCESS') ? 'text-green-400' :
                  log.includes('REQUEST') ? 'text-blue-400' :
                  log.includes('RESPONSE') ? 'text-yellow-400' :
                  'text-gray-400'
                }
              >
                {log}
              </div>
            ))}
          </div>
        </div>

        {/* Insight Panel */}
        <div className="mt-4 border border-blue-400 p-4">
          <h2 className="text-xl text-blue-400 mb-2">THE SHAPE OF THE SYSTEM</h2>
          <p className="text-sm text-gray-400">
            When you can SEE how data flows through the system - from user click,
            through the API, to the database, back through services - you understand
            the SHAPE. And when you see the shape, you can build complex things
            because you know exactly where each piece fits.
          </p>
        </div>
      </div>
    </div>
  );
}